/** Has the unattended work run — from the browser's side (OPS-01).
 *
 * `GET /api/v1/ops/liveness` was served for weeks with nothing calling it, which is this project's
 * most repeated defect: built, permissioned, documented and never asked for.
 *
 * **Three states, and the third is the one that matters most here.** This hook throws on a failed
 * read; it never turns one into a default. `return data ?? null` would make a dropped connection
 * *succeed* with nothing in it, and on a screen whose whole job is to say whether something is
 * wrong, "nothing in it" renders as "nothing is wrong". That is the worst answer a liveness
 * screen can give, and nine other read hooks here gave it before `Loaded` existed.
 *
 * **`enabled` carries the role.** The route is Admin and Approver only, so a Viewer's browser must
 * never send it: a 403 the client could have predicted is the defect UX-04 names. `retry: false`
 * because a 403 is an answer and asking again is a control that predictably fails.
 *
 * **Not cached.** A liveness answer is a statement about *now*; one served from a cache is exactly
 * the stale-figure failure, in the one place it would be read as a reason not to look.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import type { components } from "./schema";
import { refusalFrom } from "./refusal";
import { requireShape } from "./shape";

export type Liveness = components["schemas"]["OpsLivenessOut"];
export type LivenessAnchor = components["schemas"]["OpsAnchorOut"];
export type LivenessAlert = components["schemas"]["OpsAlertOut"];
export type LivenessExtraction = components["schemas"]["OpsExtractionOut"];

export const opsKeys = {
  liveness: () => ["ops", "liveness"] as const,
};

export function useLiveness(enabled: boolean) {
  return useQuery({
    queryKey: opsKeys.liveness(),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<Liveness> => {
      const { data, error, response } = await api.GET("/api/v1/ops/liveness", {});
      if (error || !data) {
        throw refusalFrom(response, error, "The liveness check could not be read.");
      }
      requireShape(
        typeof data.anchor === "object" && data.anchor !== null && Array.isArray(data.alerts),
        "The liveness check",
      );
      return data;
    },
  });
}
